import type { DefaultOptionType } from 'antd/es/select';
import type { SpaceUserGroup } from '@lens/shared';

/**
 * Группы пользователей в выпадающем списке поля типа «участник».
 *
 * Группа — заготовка, а не значение: в списке она отдельным разделом, при
 * выборе в поле попадают её участники по отдельности. Поэтому группы имеют
 * смысл только у полей, где можно выбрать несколько человек, и только пока в
 * группе кто-то есть.
 *
 * Значение опции-группы — `group:<id>`. Тот же вид понимает и сервер
 * (`spaces/user-groups.service.ts`), так что через API можно писать группу
 * прямо в поле, не перечисляя логины.
 */

export const USER_GROUP_OPTION_PREFIX = 'group:';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export function isUserGroupOption(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(USER_GROUP_OPTION_PREFIX);
}

/** Непустые группы, годные для подстановки в поле. */
function usableGroups(groups: SpaceUserGroup[]): SpaceUserGroup[] {
  return groups.filter((g) => g.members.length > 0);
}

/**
 * Список опций для поля «участник»: сверху раздел групп, ниже — участники.
 * Если групп нет или поле на одного человека — обычный плоский список, как был.
 */
export function withUserGroupOptions(
  userOptions: SelectOption[],
  groups: SpaceUserGroup[],
  isMulti: boolean,
): DefaultOptionType[] {
  const usable = isMulti ? usableGroups(groups) : [];
  if (usable.length === 0) return userOptions;
  return [
    {
      label: 'Группы',
      options: usable.map((g) => ({
        value: `${USER_GROUP_OPTION_PREFIX}${g.id}`,
        label: `${g.name} · ${g.members.length}`,
      })),
    },
    { label: 'Участники', options: userOptions },
  ];
}

/**
 * Разворачивает выбор в id пользователей: группы заменяются составом, порядок
 * выбора сохраняется, повторы схлопываются. Одиночное значение возвращается
 * как есть — группу туда выбрать нельзя.
 */
export function expandUserGroupSelection(value: unknown, groups: SpaceUserGroup[]): unknown {
  if (!Array.isArray(value)) return value;

  const membersByGroupId = new Map(
    groups.map((g) => [g.id, g.members.map((m) => m.userId)] as const),
  );
  const expanded: string[] = [];
  for (const entry of value) {
    if (isUserGroupOption(entry)) {
      const members = membersByGroupId.get(entry.slice(USER_GROUP_OPTION_PREFIX.length));
      if (members) expanded.push(...members);
      continue;
    }
    if (typeof entry === 'string') expanded.push(entry);
  }
  return [...new Set(expanded)];
}
